// Brand payment settings. Shows the card on file (held by Stripe — we never
// store card data) and lets the brand add or replace it via a hosted Stripe
// Checkout in setup mode. Reached from the sidebar and from the guided-setup
// checklist on Home. Returns here with ?card=ok after Stripe completes.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '../../theme';
import { brandApi, type BrandCard } from '../../lib/api';
import { Button } from '../../components/Button';
import { Skeleton } from '../../components/Skeleton';

function cardBrandLabel(brand: string): string {
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return map[brand?.toLowerCase()] ?? (brand ? brand[0].toUpperCase() + brand.slice(1) : 'Card');
}

export function Payment() {
  const [params, setParams] = useSearchParams();
  const [card, setCard] = useState<BrandCard | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const justAdded = params.get('card') === 'ok';

  useEffect(() => {
    brandApi
      .paymentMethod()
      .then((r) => setCard(r.card))
      .catch(() => setCard(null));
  }, []);

  // Clear the ?card= flag from the URL once shown, so a refresh doesn't keep
  // claiming "card added".
  useEffect(() => {
    if (params.get('card')) {
      const t = setTimeout(() => {
        params.delete('card');
        setParams(params, { replace: true });
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [params, setParams]);

  async function manageCard() {
    setBusy(true);
    setErr(null);
    try {
      const { url } = await brandApi.setupCardSession();
      window.location.href = url;
    } catch {
      setErr('Could not open the secure card form. Try again, or email hello@imorbis.com.');
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div
        style={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
          marginBottom: 10,
        }}
      >
        Billing
      </div>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 32,
          fontWeight: 600,
          letterSpacing: '-0.025em',
          color: tokens.color.ink[900],
          margin: 0,
        }}
      >
        Payment
      </h1>
      <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.55, color: tokens.color.ink[700], maxWidth: '52ch' }}>
        Your card is stored securely by Stripe. We never see or hold the number. Campaign
        deposits are charged to it when you launch a campaign.
      </p>

      {justAdded && (
        <div
          style={{
            marginTop: 20,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(94,252,141,0.12)',
            border: '1px solid rgba(94,252,141,0.4)',
            color: tokens.color.ink[900],
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: tokens.color.accent.tan,
              boxShadow: `0 0 8px ${tokens.color.accent.tan}`,
              flexShrink: 0,
            }}
          />
          Card saved. You're all set to launch campaigns.
        </div>
      )}

      {/* Card on file */}
      <div
        style={{
          marginTop: 28,
          background: tokens.color.paper['00'],
          border: `1px solid ${tokens.color.line[100]}`,
          borderRadius: 16,
          padding: 28,
        }}
      >
        {card === undefined ? (
          <Skeleton height={64} radius={12} />
        ) : card === null ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontFamily: tokens.font.display, fontSize: 17, fontWeight: 600, color: tokens.color.ink[900] }}>
                No card on file
              </div>
              <p style={{ marginTop: 4, fontSize: 13.5, color: tokens.color.ink[500] }}>
                Add a card to launch your first campaign.
              </p>
            </div>
            <Button onClick={manageCard} loading={busy}>
              Add card
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div
                style={{
                  width: 52,
                  height: 34,
                  borderRadius: 7,
                  background: `linear-gradient(135deg, ${tokens.color.ink[900]}, ${tokens.color.ink[700]})`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  padding: 6,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 4,
                    borderRadius: 2,
                    background: tokens.color.accent.tan,
                  }}
                />
              </div>
              <div>
                <div style={{ fontFamily: tokens.font.display, fontSize: 16, fontWeight: 600, color: tokens.color.ink[900] }}>
                  {cardBrandLabel(card.brand)} ···· {card.last4}
                </div>
                <div style={{ marginTop: 3, fontFamily: tokens.font.mono, fontSize: 12.5, color: tokens.color.ink[500] }}>
                  Expires {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                </div>
              </div>
            </div>
            <Button variant="secondary" onClick={manageCard} loading={busy}>
              Replace card
            </Button>
          </div>
        )}

        {err && <p style={{ marginTop: 16, fontSize: 13, color: tokens.color.accent.rust }}>{err}</p>}
      </div>

      <p style={{ marginTop: 18, fontSize: 12.5, color: tokens.color.ink[500], display: 'flex', alignItems: 'center', gap: 7 }}>
        <LockIcon /> Secured by Stripe. PCI compliant. Your full card number never touches Dreamlink.
      </p>
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <rect x="5" y="11" width="14" height="9" rx="2" stroke={tokens.color.ink[500]} strokeWidth="1.8" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" stroke={tokens.color.ink[500]} strokeWidth="1.8" />
    </svg>
  );
}
